import React, { useState } from "react";
import { classNames } from "maven-lib/dist/utils/misc";
import { CrButton } from "./CrButton";
import { CrDropdown } from "./CrDropdown";
import { CrIconButton } from "./CrIconButton";
import dayjs from "dayjs";

export interface CrCalendarDateLabel {
  year?: string;
  month?: string;
}

export interface CrYearMonthProps {
  selectedYearMonth?: { year?: number; month?: number };
  minDate: Date | number | "TODAY";
  maxDate: Date | number | "TODAY";
  closable: boolean;
  dateLabel?: CrCalendarDateLabel;

  onDismiss?: () => void;
  onSelectDate?: (date: Date) => void;
  onYearChange?: (date: Date) => void;
  onMonthChange?: (date: Date) => void;
}

export function CrYearMonth(props: CrYearMonthProps) {
  const {
    selectedYearMonth: yearMonth,
    minDate,
    maxDate,
    onSelectDate,
    onYearChange,
    onMonthChange,
    onDismiss,
    closable,
    dateLabel,
  } = props;
  const selectedDate: Date | null = (() => {
    if (yearMonth instanceof Date) return yearMonth;
    else if (typeof yearMonth === "object") {
      const c = dayjs();

      return dayjs(
        `${yearMonth.year || c.year()}-${String(yearMonth.month || c.month() + 1).padStart(2, "0")}-${String(c.date()).padStart(2, "0")}`
      ).toDate();
    } else {
      return null;
    }
  })();

  const minVal = Math.min(
    selectedDate ? selectedDate.getTime() : 9 * 10 ** 13,
    minDate instanceof Date
      ? minDate.getTime()
      : typeof minDate === "number"
      ? dayjs().subtract(minDate, "days").toDate().getTime()
      : new Date().getTime()
  );

  const maxVal = Math.max(
    selectedDate ? selectedDate.getTime() : 1,
    maxDate instanceof Date
      ? maxDate.getTime()
      : typeof maxDate === "number"
      ? dayjs().add(maxDate, "days").toDate().getTime()
      : new Date().getTime()
  );

  const years: number[] = (() => {
    const min = dayjs(minVal).year();
    const max = dayjs(maxVal).year();

    return "*"
      .repeat(max - min + 1)
      .split("")
      .map((_, i) => min + i)
      .reverse();
  })();

  const months: number[] = "*"
    .repeat(12)
    .split("")
    .map((_, i) => i + 1);

  const [year, setYear] = useState(selectedDate ? dayjs(selectedDate).year() : dayjs(Math.min(maxVal, new Date().getTime())).year());
  const [month, setMonth] = useState(selectedDate ? dayjs(selectedDate).month() + 1 : dayjs().month() + 1);

  return (
    <>
      <div className={classNames("cr-calendar", closable && "--closable")}>
        <div className="cr-calendar-header">
          <div className="before">
            <CrIconButton
              icon="ChevronLeft"
              disabled={`${year}-${String(month).padStart(2, "0")}-01` <= dayjs(minVal).format("YYYY-MM-01")}
              onClick={() => {
                const prev = dayjs(`${year}-${String(month).padStart(2, "0")}-01`).subtract(1, "month");

                setYear(prev.year());
                setMonth(prev.month() + 1);

                onYearChange && onYearChange(prev.toDate());
                onMonthChange && onMonthChange(prev.toDate());
              }}
            />
          </div>
          <div className="cr-calendar-navi">
            <div className="year">
              <CrDropdown
                calloutClassName="cr-calendar-navi--year"
                defaultKey={String(year)}
                items={years.map((year) => ({ key: String(year), text: String(year) }))}
                onValueChange={(val) => {
                  setYear(parseInt(val as string, 10));
                  onYearChange && onYearChange(dayjs(`${val}-${String(month).padStart(2, "0")}-01`).toDate());
                }}
              />
              {dateLabel?.year || "년"}
            </div>
            <div className="month">
              <CrDropdown
                defaultKey={String(month)}
                items={months.map((month) => ({ key: String(month), text: String(month) }))}
                onValueChange={(val) => {
                  setMonth(parseInt(val as string, 10));
                  onMonthChange && onMonthChange(dayjs(`${year}-${String(val).padStart(2, "0")}-01`).toDate());
                }}
              />{" "}
              {dateLabel?.month || "월"}
            </div>
          </div>
          <div className="next">
            <CrIconButton
              disabled={`${year}-${String(month).padStart(2, "0")}-01` >= dayjs(maxVal).format("YYYY-MM-01")}
              onClick={() => {
                const next = dayjs(`${year}-${String(month).padStart(2, "0")}-01`).add(1, "month");

                setYear(next.year());
                setMonth(next.month() + 1);

                onYearChange && onYearChange(next.toDate());
                onMonthChange && onMonthChange(next.toDate());
              }}
              icon="ChevronRight"
            />
          </div>
          {closable && (
            <div className="close">
              <CrIconButton color="primary" onClick={() => onDismiss && onDismiss()} icon="CalculatorMultiply" />
            </div>
          )}
        </div>
        <CrButton
          className="cr-calendar-enter"
          onClick={() => onSelectDate && onSelectDate(dayjs(`${year}-${String(month).padStart(2, "0")}-01`).toDate())}
        >
          Enter
        </CrButton>
      </div>
    </>
  );
}

CrYearMonth.defaultProps = {
  minDate: dayjs().subtract(90, "years").toDate(),
  maxDate: dayjs().add(2, "years").toDate(),
  closable: true,
};
